import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token'));
  const [loading, setLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  // Track if token exists - this is reactive unlike the function
  const [hasToken, setHasToken] = useState(!!(localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token')));

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;
    
    const initAuth = async () => {
      const storedToken = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
      
      // Update hasToken state
      if (isMounted) {
        setHasToken(!!storedToken);
      }
      
      // No token at all - definitively not authenticated
      if (!storedToken) {
        if (isMounted) {
          setLoading(false);
          setAuthResolved(true);
        }
        return;
      }
      
      const attemptAuth = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            timeout: 8000
          });
          
          if (isMounted) {
            setUserProfile(response.data);
            setUser({
              id: response.data.id,
              email: response.data.email,
              full_name: response.data.full_name,
              is_admin: response.data.is_admin
            });
          }
          return true; // Success
        } catch (error) {
          console.error('Auth attempt failed:', error?.message || error);
          
          // Only clear token if it's DEFINITELY invalid (401/403 from server)
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('blkmrkt_token');
            sessionStorage.removeItem('blkmrkt_token');
            if (isMounted) {
              setToken(null);
              setUser(null);
              setHasToken(false);
            }
            return true; // Don't retry - token is invalid
          }
          
          return false; // Retry on network errors
        }
      };
      
      // Try to authenticate with retries for network errors
      let success = await attemptAuth();
      while (!success && retryCount < maxRetries && isMounted) {
        retryCount++;
        console.log(`Auth retry ${retryCount}/${maxRetries}...`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
        success = await attemptAuth();
      }
      
      // ALWAYS set loading false and authResolved true when done
      if (isMounted) {
        setLoading(false);
        setAuthResolved(true);
      }
    };
    
    initAuth();
    
    return () => {
      isMounted = false;
    };
  }, [token]);

  // Helper: checks if there's a token that hasn't been invalidated
  const hasValidToken = useCallback(() => {
    return !!(localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token'));
  }, []);

  const login = async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    // Store in localStorage (persistent) or sessionStorage (session only)
    if (rememberMe) {
      localStorage.setItem('blkmrkt_token', newToken);
      sessionStorage.removeItem('blkmrkt_token');
    } else {
      sessionStorage.setItem('blkmrkt_token', newToken);
      localStorage.removeItem('blkmrkt_token');
    }
    
    setToken(newToken);
    setUser(userData);
    
    // Fetch full profile
    try {
      const profileRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUserProfile(profileRes.data);
    } catch (e) {
      console.error('Failed to fetch profile:', e);
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, {
      email: userData.email,
      password: userData.password,
      full_name: userData.full_name,
      country: userData.country || 'US',
      address: userData.address,
      apt_unit: userData.apt_unit || null,
      city: userData.city,
      state: userData.state,
      zip_code: userData.zip_code
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('blkmrkt_token');
    sessionStorage.removeItem('blkmrkt_token');
    sessionStorage.removeItem('blkmrkt_access'); // Clear drop access on logout
    setToken(null);
    setUser(null);
    setUserProfile(null);
    // Redirect to login page
    window.location.href = '/login';
  };

  const refreshProfile = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data);
      setUser({
        id: response.data.id,
        email: response.data.email,
        full_name: response.data.full_name,
        is_admin: response.data.is_admin
      });
    } catch (e) {
      console.error('Failed to refresh profile:', e);
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userProfile,
      token, 
      loading, 
      authResolved,
      hasToken,
      hasValidToken,
      login, 
      register, 
      logout, 
      refreshProfile,
      getAuthHeader 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
